import { api } from '@/boot/axios'
import { saleItemPayloadFromCartLine } from '@/utils/posLineDiscount'

class CalculationService {
  /**
   * Calculate sale pricing using the backend calculation service
   * @param {Array} items - Array of cart items
   * @param {Object} orderDiscount - Order-level discount {type: 'percentage'|'fixed', amount: number}
   * @param {Array} taxRules - Location tax rules (optional, display use)
   * @param {number} redeemPoints - Loyalty points to redeem (optional)
   * @param {number} customerId - Customer ID for loyalty calculations (optional)
   * @returns {Promise<Object>} Calculation results
   */
  async calculatePricing(items, orderDiscount = null, taxRules = [], redeemPoints = null, customerId = null) {
    // console.log(items)
    void taxRules

    try {
      // Transform cart items to backend format
      const backendItems = items
        .filter(item => !(item.metadata?.redeeming_for_points || item.redeeming_for_points))
        .map(item => {
          const payload = saleItemPayloadFromCartLine(item)
          return {
            product_id: payload.product_id,
            quantity: payload.quantity,
            price: payload.price != null ? Number(payload.price) : null,
            discount_type: payload.discount_type,
            discount_amount: payload.discount_amount,
            modifiers: payload.modifiers,
            ...(payload.extended_price != null ? { extended_price: payload.extended_price } : {})
          }
        })

      // Transform order discount to backend format
      const backendOrderDiscount = orderDiscount ? {
        type: orderDiscount.type === '%' ? 'percentage' : 'fixed',
        amount: orderDiscount.amount
      } : null

      const requestData = {
        items: backendItems,
        discount_type: backendOrderDiscount?.type,
        discount_amount: backendOrderDiscount?.amount
      }

      // Add loyalty points redemption if provided
      if (redeemPoints && redeemPoints > 0) {
        requestData.redeem_points = redeemPoints
      }

      // Add customer ID for loyalty calculations if provided
      if (customerId) {
        requestData.customer_id = customerId
      }

      const response = await api.post('/sales/calculate-pricing', requestData)

      return response.data
    } catch (error) {
      console.error('Error calculating pricing:', error)
      throw error
    }
  }

  /**
   * Map GET /cart (or cart mutation) `summary` to the same shape as getRealTimeCalculation (totals-only).
   * @param {Object} summary
   * @returns {Object|null}
   */
  fromCartSummary (summary) {
    if (!summary) {
      return null
    }
    return {
      subtotal: summary.subtotal,
      baseSubtotal: summary.base_subtotal ?? summary.subtotal,
      taxAmount: summary.tax_amount,
      taxBreakdown: summary.tax_breakdown || [],
      total: summary.total,
      totalDiscounts: summary.total_discounts ?? 0,
      expectedPoints: summary.expected_points ?? 0,
      items: [],
      breakdown: {
        lineDiscountsApplied: 0,
        orderDiscountApplied: 0,
        totalDiscounts: summary.total_discounts ?? 0
      }
    }
  }

  /**
   * Transform backend calculation results to frontend format
   * @param {Object} backendCalculation - Backend calculation results
   * @returns {Object} Frontend-friendly calculation results
   */
  transformCalculationResults(backendCalculation) {
    const { calculation, summary } = backendCalculation

    return {
      subtotal: summary.subtotal,
      baseSubtotal: summary.base_subtotal || summary.subtotal,
      taxAmount: summary.tax_amount,
      taxBreakdown: calculation.tax_breakdown || [],
      total: summary.total,
      totalDiscounts: summary.total_discounts,
      expectedPoints: summary.expected_points || 0,
      items: calculation.items.map(item => ({
        productId: item.product_id ?? item.metadata?.product_id,
        productName: item.product_name,
        quantity: item.quantity,
        price: item.price,
        originalSubtotal: item.original_subtotal,
        lineDiscountAmount: item.line_discount_amount,
        proratedDiscountAmount: item.prorated_discount_amount,
        subtotalAfterDiscounts: item.subtotal_after_prorated_discount,
        isTaxable: item.is_taxable,
        taxBreakdown: item.tax_breakdown || [],
        taxableAmount: item.taxable_amount,
        taxAmount: item.tax_amount,
        itemTotal: item.item_total
      })),
      breakdown: {
        lineDiscountsApplied: calculation.calculation_breakdown.line_discounts_applied,
        orderDiscountApplied: calculation.calculation_breakdown.order_discount_applied,
        totalDiscounts: calculation.calculation_breakdown.total_discounts
      }
    }
  }

  /**
   * Get real-time calculation for the cart
   * @param {Array} cart - Cart items
   * @param {Object} saleDiscount - Sale discount
   * @param {Array} taxRules - Location tax rules
   * @param {number} redeemPoints - Loyalty points to redeem (optional)
   * @param {number} customerId - Customer ID for loyalty calculations (optional)
   * @returns {Promise<Object>} Calculation results
   */
  async getRealTimeCalculation(cart, saleDiscount, taxRules = [], redeemPoints = null, customerId = null) {
    if (cart.length === 0) {
      return {
        subtotal: 0,
        taxAmount: 0,
        total: 0,
        totalDiscounts: 0,
        items: [],
        breakdown: {
          lineDiscountsApplied: 0,
          orderDiscountApplied: 0,
          totalDiscounts: 0
        }
      }
    }

    const orderDiscount = saleDiscount.amount > 0 ? {
      type: saleDiscount.type,
      amount: saleDiscount.amount
    } : null

    try {
      const backendResult = await this.calculatePricing(cart, orderDiscount, taxRules, redeemPoints, customerId)
      return this.transformCalculationResults(backendResult)
    }
    catch (error) {
      console.error('Error getting real-time calculation:', error)
      throw error
    }
  }
}

export default new CalculationService()
